"use client";

import { formatPhoneNumber } from "@/helpers/formatPhone";
import { Firma, getFirmaDisplayName } from "@/lib/consts";
import { useLocale } from "@/components/i18n/LocaleProvider";
import { useMessages } from "@/lib/i18n/useMessages";
import { fillStringTemplate } from "@/lib/utils/fillStringTemplate";
import { Location } from "@/lib/locationsData";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "motion/react";
import { SLCopyToClipboardButton } from "../buttons/SLCopyToClipboardButton";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

interface SLCardAdressProps {
  location: Location;
  firmaId?: Firma;
}

export const SLCardAdress = ({
  location,
  firmaId = Firma.SolvingLegal,
}: SLCardAdressProps) => {
  const { locale } = useLocale();
  const { common } = useMessages();
  const firmLine = getFirmaDisplayName(locale, firmaId);
  const firmLineHtml =
    firmaId === Firma.SolvingTax && firmLine.startsWith("Solving Tax")
      ? `Solving Tax<br/>${firmLine.slice("Solving Tax".length).replace(/^\s+/, "")}`
      : firmLine;
  const officeTitle = common.officeLocation.title.replace(
    "{city}",
    location.fullName,
  );
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  const variants = {
    initial: { opacity: isMobile ? 1 : 0 },
    hover: { opacity: 1 },
  };

  const phone = location.city.includes("Landau")
    ? location.phone.moritz
    : location.phone.hubi;

  const structredPhone = formatPhoneNumber(phone);
  const phoneLineHtml = fillStringTemplate(common.officeLocation.phoneLine, {
    phone: structredPhone,
  });

  const countryLine = common.officeLocation.countryGermany;
  const addressBlockHtml = `${firmLineHtml}<br/>${location.adress}<br/>${countryLine}`;
  const copyText = `${firmLine} \n${location.street} ${location.houseNumber}\n${location.postalCode} ${location.city}\n${countryLine}`;

  return (
    <Card
      sx={{
        backgroundColor: "transparent",
        position: "relative",
      }}
      component={motion.div}
      initial="initial"
      whileHover="hover"
    >
      <CardHeader
        title={officeTitle}
        sx={{
          pt: 0,
          px: 0,
        }}
      />
      <CardContent
        sx={{
          px: 0,
        }}
      >
        <Stack direction="row" gap={2}>
          <SLHtmlFromLangFile htmlString={addressBlockHtml} />

          <Box component={motion.div} variants={variants}>
            <SLCopyToClipboardButton
              size="medium"
              variant="text"
              color="success"
              iconOnly={true}
              text={copyText}
              tooltipText={common.accessibility.copyAddress}
              successMessage={common.accessibility.copyAddressSuccess}
            />
          </Box>
        </Stack>
        <Stack direction="row" gap={2}>
          <Link href={`tel:${phone}`}>
            <SLHtmlFromLangFile htmlString={phoneLineHtml} />
          </Link>
        </Stack>
      </CardContent>
    </Card>
  );
};
